import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { FaSearch } from 'react-icons/fa';
import { State, City } from 'country-state-city';

import Wrapper from '../../components/Wrapper';
import axiosInstance from '../../utils/axios';
import { fetchPartners } from '../../store/features/partnersSlice';

const ClinicSchema = Yup.object().shape({
  clinic_code: Yup.string().required('Clinic Code is required'),
  admin_id: Yup.string().required('Admin/Partner is required'),
  customer_type: Yup.string().required('Customer Type is required'),
  custom_customer_type: Yup.string().when('customer_type', {
    is: (val) => val === 'Other',
    then: (schema) => schema.required('Please specify the customer type'),
    otherwise: (schema) => schema.notRequired(),
  }),
  state: Yup.string().required('State is required'),
  district: Yup.string().required('District is required'),
  status: Yup.string().required('Status is required'),
  contact: Yup.string().nullable(),
  latitude: Yup.number().typeError('Must be a number').nullable(),
  longitude: Yup.number().typeError('Must be a number').nullable(),
  field_officer_ids: Yup.array().of(Yup.string()),
  address: Yup.string().required('Address is required'),
  pincode: Yup.string().matches(/^[0-9]{6}$/, 'Must be exactly 6 digits').required('Pincode is required'),
  about: Yup.string().nullable()
});

const AddClinic = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const partnersList = useSelector((state) => state.partners?.partners ?? []);
  const [displayedFieldOfficers, setDisplayedFieldOfficers] = useState([]);

  const [partnerDropdownOpen, setPartnerDropdownOpen] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState('');
  const partnerDropdownRef = useRef(null);

  const [foDropdownOpen, setFoDropdownOpen] = useState(false);
  const [foSearch, setFoSearch] = useState('');
  const foDropdownRef = useRef(null);

  const indiaStates = State.getStatesOfCountry('IN');
  const [availableCities, setAvailableCities] = useState([]);

  useEffect(() => {
    dispatch(fetchPartners({ page: 1, limit: 500 }));
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (partnerDropdownRef.current && !partnerDropdownRef.current.contains(e.target)) {
        setPartnerDropdownOpen(false);
        setPartnerSearch('');
      }
      if (foDropdownRef.current && !foDropdownRef.current.contains(e.target)) {
        setFoDropdownOpen(false);
        setFoSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAdminSelect = async (adminId, setFieldValue) => {
    setFieldValue('admin_id', adminId);
    setFieldValue('field_officer_ids', []);
    setPartnerDropdownOpen(false);
    setPartnerSearch('');

    try {
      const { data } = await axiosInstance.get(`/super-admin/${adminId}/field-officers`);
      setDisplayedFieldOfficers(Array.isArray(data?.fieldOfficers) ? data.fieldOfficers : []);
    } catch (error) {
      console.error('Failed to fetch field officers:', error);
      setDisplayedFieldOfficers([]);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = { ...values };
      if (payload.customer_type === 'Other') {
        payload.customer_type = payload.custom_customer_type;
      }
      
      delete payload.custom_customer_type;

      await axiosInstance.post('/super-admin/clinics', payload);
      
      Swal.fire('Success', 'Clinic created successfully', 'success');
      resetForm();
      navigate('/clinics');
    } catch (error) {
      console.error('Error creating clinic:', error);
      const errorMsg = error.response?.data?.message || 'Failed to create clinic.';
      Swal.fire('Error', errorMsg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredPartners = partnersList.filter((p) => {
    const label = (p.entity_name || p.name || String(p.admin_id)).toLowerCase();
    return label.includes(partnerSearch.toLowerCase());
  });

  const filteredFos = displayedFieldOfficers.filter((fo) =>
    (fo.name || '').toLowerCase().includes(foSearch.toLowerCase())
  );

  return (
    <Wrapper>
      <div className="py-4 w-100 max-w-4xl mx-auto">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-slate-800">Add New Soil Doctor Clinic</h2>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <Formik
            initialValues={{
              clinic_code: '',
              admin_id: '',
              field_officer_ids: [],
              customer_type: '',
              custom_customer_type: '',
              state: '',
              district: '',
              status: 'FUNCTIONAL',
              contact: '',
              latitude: '',
              longitude: '',
              address: '',
              pincode: '',
              about: '',
            }}
            validationSchema={ClinicSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, isSubmitting, touched, errors }) => {
              
              const selectedPartnerObj = partnersList.find(p => String(p.admin_id) === String(values.admin_id));
              const selectedPartnerLabel = selectedPartnerObj ? (selectedPartnerObj.entity_name || selectedPartnerObj.name || selectedPartnerObj.admin_id) : 'Select Admin / Partner';

              return (
                <Form>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Clinic Code */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Clinic Code <span className="text-red-500">*</span></label>
                      <Field type="text" name="clinic_code" className={`w-full border rounded p-2 text-sm outline-none focus:border-emerald-500 ${touched.clinic_code && errors.clinic_code ? 'border-red-500' : 'border-slate-300'}`} placeholder="e.g. SDC-LUD-001" />
                      <ErrorMessage name="clinic_code" component="div" className="text-red-500 text-xs mt-1" />
                    </div>

                    {/* Admin Dropdown */}
                    <div className="relative w-full" ref={partnerDropdownRef}>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Admin / Partner <span className="text-red-500">*</span></label>
                      <button
                        type="button"
                        className={`flex items-center justify-between w-full border rounded p-2 text-sm bg-white text-left focus:border-emerald-500 outline-none ${touched.admin_id && errors.admin_id ? 'border-red-500' : 'border-slate-300'}`}
                        onClick={() => {
                          setPartnerDropdownOpen((o) => !o);
                          setPartnerSearch('');
                        }}
                      >
                        <span className="truncate flex-1">{selectedPartnerLabel}</span>
                        <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <ErrorMessage name="admin_id" component="div" className="text-red-500 text-xs mt-1" />

                      {partnerDropdownOpen && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg">
                          <div className="p-2 border-b border-slate-100 relative">
                            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                            <input
                              autoFocus
                              type="text"
                              placeholder="Search admin..."
                              className="pl-8 pr-3 py-1.5 w-full rounded border border-slate-300 text-sm outline-none focus:border-emerald-500"
                              value={partnerSearch}
                              onChange={(e) => setPartnerSearch(e.target.value)}
                            />
                          </div>
                          <ul className="max-h-52 overflow-y-auto py-1">
                            {filteredPartners.length > 0 ? (
                              filteredPartners.map(p => (
                                <li
                                  key={p.admin_id}
                                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 ${String(values.admin_id) === String(p.admin_id) ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-slate-700'}`}
                                  onClick={() => handleAdminSelect(p.admin_id, setFieldValue)}
                                >
                                  {p.entity_name || p.name || p.admin_id}
                                </li>
                              ))
                            ) : (
                              <li className="px-3 py-2 text-sm text-slate-400 italic">No admins found</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Multi-select Field Officer Dropdown */}
                    <div className="relative w-full" ref={foDropdownRef}>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Field Officers</label>
                      <button
                        type="button"
                        disabled={!values.admin_id}
                        className={`flex items-center justify-between w-full border border-slate-300 rounded p-2 text-sm bg-white text-left focus:border-emerald-500 outline-none ${!values.admin_id ? 'bg-slate-100 cursor-not-allowed' : ''}`}
                        onClick={() => {
                          setFoDropdownOpen((o) => !o);
                          setFoSearch('');
                        }}
                      >
                        <span className="truncate flex-1">
                          {!values.admin_id 
                            ? 'Select an Admin first' 
                            : values.field_officer_ids.length > 0 
                              ? `${values.field_officer_ids.length} Officer(s) Selected` 
                              : 'Select Field Officers (Optional)'}
                        </span>
                        <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {foDropdownOpen && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg">
                          <div className="p-2 border-b border-slate-100 relative">
                            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                            <input
                              autoFocus
                              type="text"
                              placeholder="Search field officer..."
                              className="pl-8 pr-3 py-1.5 w-full rounded border border-slate-300 text-sm outline-none focus:border-emerald-500"
                              value={foSearch}
                              onChange={(e) => setFoSearch(e.target.value)}
                            />
                          </div>
                          <ul className="max-h-52 overflow-y-auto py-1">
                            {filteredFos.length > 0 ? (
                              filteredFos.map(fo => {
                                const isSelected = values.field_officer_ids.includes(fo.field_officer_id);
                                return (
                                  <li
                                    key={fo.field_officer_id}
                                    className={`px-3 py-2 text-sm cursor-pointer flex justify-between items-center hover:bg-emerald-50 ${isSelected ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-slate-700'}`}
                                    onClick={() => {
                                      const currentSelected = [...values.field_officer_ids];
                                      if (isSelected) {
                                        setFieldValue('field_officer_ids', currentSelected.filter(id => id !== fo.field_officer_id));
                                      } else {
                                        setFieldValue('field_officer_ids', [...currentSelected, fo.field_officer_id]);
                                      }
                                    }}
                                  >
                                    <span>{fo.name}</span>
                                    {isSelected && <span className="text-emerald-600 font-bold">✓</span>}
                                  </li>
                                );
                              })
                            ) : (
                              <li className="px-3 py-2 text-sm text-slate-400 italic">No field officers found for this admin</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Customer Type Dropdown */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Customer Type <span className="text-red-500">*</span></label>
                      <Field 
                        as="select" 
                        name="customer_type" 
                        className={`w-full border rounded p-2 text-sm outline-none focus:border-emerald-500 bg-white ${touched.customer_type && errors.customer_type ? 'border-red-500' : 'border-slate-300'}`}
                        onChange={(e) => {
                          setFieldValue('customer_type', e.target.value);
                          if (e.target.value !== 'Other') {
                            setFieldValue('custom_customer_type', '');
                          }
                        }}
                      >
                        <option value="">Select Customer Type</option>
                        <option value="Private Company">Private Company</option>
                        <option value="Agri Retailer / Krishi Kendra">Agri Retailer / Krishi Kendra</option>
                        <option value="FPC / FPO">FPC / FPO</option>
                        <option value="NGO">NGO</option>
                        <option value="Individual / Entrepreneur">Individual / Entrepreneur</option>
                        <option value="Foundation">Foundation</option>
                        <option value="Corporate CSR">Corporate CSR</option>
                        <option value="Other">Other (Please Specify)</option>
                      </Field>
                      <ErrorMessage name="customer_type" component="div" className="text-red-500 text-xs mt-1" />
                    </div>

                    {values.customer_type === 'Other' && (
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Specify Customer Type <span className="text-red-500">*</span></label>
                        <Field type="text" name="custom_customer_type" className={`w-full border rounded p-2 text-sm outline-none focus:border-emerald-500 ${touched.custom_customer_type && errors.custom_customer_type ? 'border-red-500' : 'border-slate-300'}`} placeholder="Enter customer type" />
                        <ErrorMessage name="custom_customer_type" component="div" className="text-red-500 text-xs mt-1" />
                      </div>
                    )}

                    {/* State Dropdown */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">State <span className="text-red-500">*</span></label>
                      <select
                        name="state"
                        className={`w-full border rounded p-2 text-sm outline-none focus:border-emerald-500 bg-white ${touched.state && errors.state ? 'border-red-500' : 'border-slate-300'}`}
                        value={values.state}
                        onChange={(e) => {
                          const selectedStateName = e.target.value;
                          setFieldValue('state', selectedStateName);
                          setFieldValue('district', ''); 
                          
                          const stateObj = indiaStates.find(s => s.name === selectedStateName);
                          if (stateObj) {
                            setAvailableCities(City.getCitiesOfState('IN', stateObj.isoCode));
                          } else {
                            setAvailableCities([]);
                          }
                        }}
                      >
                        <option value="">Select State</option>
                        {indiaStates.map(state => (
                          <option key={state.isoCode} value={state.name}>
                            {state.name}
                          </option>
                        ))}
                      </select>
                      <ErrorMessage name="state" component="div" className="text-red-500 text-xs mt-1" />
                    </div>

                    {/* District Dropdown*/}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">District / City <span className="text-red-500">*</span></label>
                      <select
                        name="district"
                        disabled={!values.state}
                        className={`w-full border rounded p-2 text-sm outline-none focus:border-emerald-500 bg-white ${!values.state ? 'bg-slate-100 cursor-not-allowed' : ''} ${touched.district && errors.district ? 'border-red-500' : 'border-slate-300'}`}
                        value={values.district}
                        onChange={(e) => setFieldValue('district', e.target.value)}
                      >
                        <option value="">Select District</option>
                        {availableCities.map((city, idx) => (
                          <option key={`${city.name}-${idx}`} value={city.name}>
                            {city.name}
                          </option>
                        ))}
                      </select>
                      <ErrorMessage name="district" component="div" className="text-red-500 text-xs mt-1" />
                    </div>

                    {/* Address */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Full Address <span className="text-red-500">*</span></label>
                      <Field 
                        as="textarea" 
                        rows="2"
                        name="address" 
                        className={`w-full border rounded p-2 text-sm outline-none focus:border-emerald-500 ${touched.address && errors.address ? 'border-red-500' : 'border-slate-300'}`} 
                        placeholder="Enter full clinic address"
                      />
                      <ErrorMessage name="address" component="div" className="text-red-500 text-xs mt-1" />
                    </div>

                    {/* Pincode */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Pincode <span className="text-red-500">*</span></label>
                      <Field 
                        type="text" 
                        name="pincode" 
                        maxLength="6"
                        className={`w-full border rounded p-2 text-sm outline-none focus:border-emerald-500 ${touched.pincode && errors.pincode ? 'border-red-500' : 'border-slate-300'}`} 
                        placeholder="e.g. 141001"
                      />
                      <ErrorMessage name="pincode" component="div" className="text-red-500 text-xs mt-1" />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Contact Number</label>
                      <Field type="text" name="contact" className="w-full border border-slate-300 rounded p-2 text-sm outline-none focus:border-emerald-500" />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                      <Field as="select" name="status" className="w-full border border-slate-300 rounded p-2 text-sm outline-none focus:border-emerald-500 bg-white">
                        <option value="FUNCTIONAL">Functional</option>
                        <option value="NON_FUNCTIONAL">Non-Functional</option>
                      </Field>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Latitude <span className="text-red-500">*</span></label>
                      <Field type="number" step="any" name="latitude" className="w-full border border-slate-300 rounded p-2 text-sm outline-none focus:border-emerald-500" placeholder="e.g. 30.9009" />
                      <ErrorMessage name="latitude" component="div" className="text-red-500 text-xs mt-1" />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Longitude <span className="text-red-500">*</span></label>
                      <Field type="number" step="any" name="longitude" className="w-full border border-slate-300 rounded p-2 text-sm outline-none focus:border-emerald-500" placeholder="e.g. 75.8572" />
                      <ErrorMessage name="longitude" component="div" className="text-red-500 text-xs mt-1" />
                    </div>
                  </div>

                  {/* About */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-slate-700 mb-1">About Clinic</label>
                      <Field 
                        as="textarea" 
                        rows="3"
                        name="about" 
                        className="w-full border border-slate-300 rounded p-2 text-sm outline-none focus:border-emerald-500" 
                        placeholder="Notes about working hours, timings, charges, etc."
                      />
                      <ErrorMessage name="about" component="div" className="text-red-500 text-xs mt-1" />
                    </div>

                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => navigate('/super-admin/clinics')}
                      className="px-6 py-2 border border-slate-300 text-slate-700 rounded-md font-medium hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-6 py-2 bg-emerald-600 text-white rounded-md font-medium hover:bg-emerald-700 disabled:bg-slate-400 transition-colors flex items-center gap-2"
                    >
                      {isSubmitting ? 'Saving...' : 'Save Clinic'}
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </Wrapper>
  );
};

export default AddClinic;